<template>
    <div :class="{ 'tw:pb-24': selectedCount > 0 }">
        <ListWrapper
            :loading="loading"
            :error="error"
            :is-empty="abonos.length === 0"
            :meta="meta"
            :page="page"
            :per-page="perPage"
            @fetch="fetchAbonos($event, perPage)"
        >
            <template #filters>
                <CardFront custom-classes="tw:mb-3 tw:py-4 tw:px-4">
                    <div class="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-[1fr_220px] tw:md:gap-3">
                        <InputText
                            :value="search"
                            name="search-abonos"
                            :label="$t('perfil.buscar_abonos')"
                            @input="$emit('update:search', $event)"
                        />
                        <InputMulti
                            v-if="listas.length > 0"
                            :value="selectedListaId"
                            :options="listas"
                            label-key="nombre"
                            :label="$t('perfil.filtrar_lista_abonados')"
                            :multiple="false"
                            custom-class="tw:w-full"
                            @input="$emit('update:lista-id', $event)"
                        />
                    </div>
                </CardFront>
            </template>

            <template #error="{ error: err }">
                <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                    <i class="material-icons tw:text-5xl tw:text-red-300">error_outline</i>
                    <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.abonos_error_titulo') }}</p>
                    <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ err }}</p>
                </CardFront>
            </template>

            <template #empty>
                <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                    <i class="material-icons tw:text-5xl tw:text-slate-300">confirmation_number</i>
                    <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.abonos_sin_activos') }}</p>
                    <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ $t('perfil.abonos_sin_activos_descripcion') }}</p>
                </CardFront>
            </template>

            <AbonoCard
                v-for="abono in abonos"
                :key="abono.id"
                :abono="abono"
                :detalle="detalleCache.get(abono.id) || null"
                :loading-detalle="detalleLoadingIds.has(abono.id)"
                :loading-desmarcar="desmarcarLoadingIds.has(abono.id)"
                :is-selected="plazo => isSelected(abono.id, plazo.id)"
                @request-desmarcar="onRequestDesmarcar"
                @expand="loadDetalle"
                @liberar-asiento="$emit('liberar-asiento', $event)"
                @cambiar-asiento="$emit('cambiar-asiento', $event)"
                @invitaciones="$emit('invitaciones', $event)"
                @ceder-asiento="$emit('ceder-asiento', $event)"
                @ver-familias="$emit('ver-familias', $event)"
                @plazo-toggled="$emit('plazo-toggled', $event)"
                @familia-created="$emit('familia-created', $event)"
            />
        </ListWrapper>

        <SelectionBar
            v-if="selectedCount > 0"
            :count="selectedCount"
            :total="totalSeleccionado"
            @open="showPagoPlazosModal = true"
        />

        <PaymentModal
            :show="showPagoPlazosModal"
            :plazos="Object.values(selectedPlazos)"
            :total="totalSeleccionado"
            :loading="pagoLoading"
            :supports3DS="true"
            @close="showPagoPlazosModal = false"
            @pago-completado="onPagoCompletado"
        />

        <!-- Modal de confirmación — instancia única compartida por todas las AbonoCard -->
        <BaseModalFront
            :show="pendingDesmarcarId !== null"
            :title="$t('perfil.desactivar_renovacion_auto')"
            :subtitle="$t('perfil.accion_irreversible')"
            :message="$t('perfil.desactivar_renovacion_auto_aviso')"
            :confirm-text="$t('perfil.si_desactivar')"
            :cancel-text="$t('perfil.cancelar')"
            confirm-variant="danger"
            :close-on-backdrop="!isConfirmingDesmarcar"
            :close-on-escape="!isConfirmingDesmarcar"
            :confirm-disabled="isConfirmingDesmarcar"
            @close="onCancelConfirm"
            @confirm="onConfirmDesmarcar"
        />
    </div>
</template>

<script setup>
    import { shallowRef, ref, computed, watch } from 'vue';
    import AbonoCard from './Card.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import InputMulti from '@/modules/front/ui/InputMulti.vue';
    import ListWrapper from '@/modules/front/ui/ListWrapper.vue';
    import SelectionBar from '../plazos/SelectionBar.vue';
    import PaymentModal from '../plazos/PaymentModal.vue';
    import { useAbonos } from '@/composables/front/perfil/useAbonos';

    const props = defineProps({
        abonos: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        search: {
            type: String,
            default: '',
        },
        meta: {
            type: Object,
            required: true,
        },
        page: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
        },
        fetchAbonos: {
            type: Function,
            required: true,
        },
        desmarcarRenovacion: {
            type: Function,
            required: true,
        },
        detalleCache: {
            type: Map,
            required: true,
        },
        detalleLoadingIds: {
            type: Set,
            required: true,
        },
        loadDetalle: {
            type: Function,
            required: true,
        },
        desmarcarLoadingIds: {
            type: Set,
            required: true,
        },
        isSelected: {
            type: Function,
            required: true,
        },
        selectedCount: {
            type: Number,
            default: 0,
        },
        totalSeleccionado: {
            type: Number,
            default: 0,
        },
        pagoLoading: {
            type: Boolean,
            default: false,
        },
        selectedPlazos: {
            type: Object,
            default: () => ({}),
        },
        listas: {
            type: Array,
            default: () => [],
        },
        listasLoading: {
            type: Boolean,
            default: false,
        },
        selectedListaId: {
            type: [Number, String],
            default: null,
        },
    });



    const emit = defineEmits(['liberar-asiento', 'cambiar-asiento', 'ceder-asiento', 'invitaciones', 'ver-familias', 'plazo-toggled', 'pago-completado', 'update:search', 'update:lista-id', 'familia-created']);

    const showPagoPlazosModal = ref(false);

    const pendingDesmarcarId = shallowRef(null);
    const isConfirmingDesmarcar = computed(() =>
        pendingDesmarcarId.value !== null &&
        props.desmarcarLoadingIds.has(pendingDesmarcarId.value)
    );

    const onRequestDesmarcar = abonadoId => { pendingDesmarcarId.value = abonadoId; };
    const onCancelConfirm = () => { pendingDesmarcarId.value = null; };
    const onConfirmDesmarcar = () => {
        if (pendingDesmarcarId.value === null) return;
        props.desmarcarRenovacion(pendingDesmarcarId.value);
    };

    const onPagoCompletado = () => {
        showPagoPlazosModal.value = false;
        emit('pago-completado');
    };

    watch(() => props.desmarcarLoadingIds, loadingIds => {
        if (pendingDesmarcarId.value !== null && !loadingIds.has(pendingDesmarcarId.value)) {
            pendingDesmarcarId.value = null;
        }
    }, { deep: true });
</script>
